import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  CalendarIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon,
  BanknotesIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import {
  useRecurringContracts,
  useRecurringStats,
  useCreateRecurringContract,
  useUpdateRecurringContractStatus,
  useGenerateRecurringInvoice,
  useDeleteRecurringContract,
} from '@/hooks/useQonto'
import {
  frequencyLabels,
  recurringStatusLabels,
  recurringStatusColors,
  type RecurringContractFormData,
  type RecurrenceFrequency,
} from '@/types/qonto'

export default function RecurringPaymentsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const { data, isLoading } = useRecurringContracts({
    status: selectedStatus === 'all' ? undefined : selectedStatus as any,
  })
  const { data: stats } = useRecurringStats()
  const updateStatus = useUpdateRecurringContractStatus()
  const generateInvoice = useGenerateRecurringInvoice()
  const deleteContract = useDeleteRecurringContract()

  const handleStatusChange = async (id: string, status: 'active' | 'paused' | 'cancelled' | 'completed') => {
    try {
      await updateStatus.mutateAsync({ id, status })
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handleGenerateInvoice = async (contractId: string) => {
    try {
      await generateInvoice.mutateAsync(contractId)
      alert('Facture generee avec succes')
    } catch (error) {
      console.error('Failed to generate invoice:', error)
      alert('Erreur lors de la generation de la facture')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Etes-vous sur de vouloir supprimer ce contrat ?')) return

    try {
      await deleteContract.mutateAsync(id)
    } catch (error) {
      console.error('Failed to delete contract:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/comptabilite"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              Comptabilite
            </Link>
            <span className="text-gray-400">/</span>
            <h1 className="text-2xl font-bold text-gray-900">Paiements recurrents</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Gerez vos contrats d'abonnement et factures recurrentes
          </p>
        </div>
        <Button
          variant="primary"
          icon={PlusIcon}
          onClick={() => setShowCreateModal(true)}
        >
          Nouveau contrat
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Contrats actifs</p>
                <p className="mt-2 text-3xl font-bold text-mint-600">
                  {stats.activeContracts}
                </p>
              </div>
              <div className="p-3 bg-mint-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-mint-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">MRR (Revenu mensuel)</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {stats.totalMonthlyRevenue.toFixed(2)} €
                </p>
              </div>
              <div className="p-3 bg-gold-100 rounded-lg">
                <BanknotesIcon className="w-6 h-6 text-gold-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">A facturer</p>
                <p className="mt-2 text-3xl font-bold text-orange-600">
                  {stats.upcomingInvoices}
                </p>
                <p className="mt-1 text-xs text-gray-500">7 prochains jours</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En pause</p>
                <p className="mt-2 text-3xl font-bold text-gray-600">
                  {stats.pausedContracts}
                </p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <PauseIcon className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Statut :</label>
          <div className="flex gap-2">
            {['all', 'active', 'paused', 'cancelled', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  selectedStatus === status
                    ? 'bg-mint-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'Tous' : recurringStatusLabels[status as keyof typeof recurringStatusLabels]}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Contracts List */}
      {isLoading ? (
        <Card>
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : !data || data.contracts.length === 0 ? (
        <Card>
          <EmptyState
            icon={CalendarIcon}
            title="Aucun contrat recurrent"
            description="Creez votre premier contrat pour commencer a generer des factures automatiquement"
            action={
              <Button
                variant="primary"
                icon={PlusIcon}
                onClick={() => setShowCreateModal(true)}
              >
                Creer un contrat
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.contracts.map((contract) => (
            <Card key={contract.id}>
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {contract.title}
                      </h3>
                      <Badge
                        className={`${recurringStatusColors[contract.status].bg} ${recurringStatusColors[contract.status].text}`}
                      >
                        {recurringStatusLabels[contract.status]}
                      </Badge>
                    </div>
                    {contract.description && (
                      <p className="mt-1 text-sm text-gray-600">{contract.description}</p>
                    )}
                  </div>
                </div>

                {/* Client */}
                {contract.client && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Client:</span>
                    <span className="font-medium text-gray-900">
                      {contract.client.full_name}
                    </span>
                    {contract.client.company && (
                      <span className="text-gray-500">({contract.client.company})</span>
                    )}
                  </div>
                )}

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Montant HT:</span>
                    <p className="font-semibold text-gray-900">
                      {(contract.amount_ht_cents / 100).toFixed(2)} €
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Frequence:</span>
                    <p className="font-semibold text-gray-900">
                      {frequencyLabels[contract.frequency]}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Debut:</span>
                    <p className="font-semibold text-gray-900">
                      {new Date(contract.start_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Prochaine facture:</span>
                    <p className="font-semibold text-gray-900">
                      {new Date(contract.next_invoice_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                  {contract.status === 'active' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={DocumentTextIcon}
                        onClick={() => handleGenerateInvoice(contract.id)}
                        loading={generateInvoice.isPending}
                      >
                        Generer facture
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={PauseIcon}
                        onClick={() => handleStatusChange(contract.id, 'paused')}
                      >
                        Mettre en pause
                      </Button>
                    </>
                  )}
                  {contract.status === 'paused' && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={PlayIcon}
                      onClick={() => handleStatusChange(contract.id, 'active')}
                    >
                      Reactiver
                    </Button>
                  )}
                  {contract.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={XMarkIcon}
                      onClick={() => handleStatusChange(contract.id, 'cancelled')}
                    >
                      Annuler
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(contract.id)}
                    className="ml-auto text-red-600 hover:text-red-700"
                  >
                    Supprimer
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateContractModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  )
}

function CreateContractModal({ onClose }: { onClose: () => void }) {
  const [formData, setFormData] = useState<Partial<RecurringContractFormData>>({
    frequency: 'monthly',
    tva_rate: 20,
    auto_generate: true,
    days_before_due: 15,
    status: 'active',
  })
  const createContract = useCreateRecurringContract()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await createContract.mutateAsync(formData as RecurringContractFormData)
      onClose()
    } catch (error) {
      console.error('Failed to create contract:', error)
      alert('Erreur lors de la creation du contrat')
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Nouveau contrat recurrent"
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Titre du contrat"
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Montant HT (€)"
            type="number"
            step="0.01"
            value={formData.amount_ht_cents ? formData.amount_ht_cents / 100 : ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                amount_ht_cents: Math.round(parseFloat(e.target.value) * 100),
              })
            }
            required
          />

          <Input
            label="Taux TVA (%)"
            type="number"
            step="0.1"
            value={formData.tva_rate || 20}
            onChange={(e) =>
              setFormData({ ...formData, tva_rate: parseFloat(e.target.value) })
            }
            required
          />
        </div>

        <Select
          label="Frequence"
          value={formData.frequency || 'monthly'}
          onChange={(e) =>
            setFormData({ ...formData, frequency: e.target.value as RecurrenceFrequency })
          }
          options={[
            { value: 'monthly', label: 'Mensuel' },
            { value: 'quarterly', label: 'Trimestriel' },
            { value: 'biannual', label: 'Semestriel' },
            { value: 'annual', label: 'Annuel' },
          ]}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Date de debut"
            type="date"
            value={formData.start_date || ''}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />

          <Input
            label="Date de fin (optionnelle)"
            type="date"
            value={formData.end_date || ''}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value || null })}
          />
        </div>

        <Input
          label="Prochaine date de facturation"
          type="date"
          value={formData.next_invoice_date || ''}
          onChange={(e) =>
            setFormData({ ...formData, next_invoice_date: e.target.value })
          }
          required
        />

        <Input
          label="Delai de paiement (jours)"
          type="number"
          value={formData.days_before_due || 15}
          onChange={(e) =>
            setFormData({ ...formData, days_before_due: parseInt(e.target.value) })
          }
          required
        />

        <Input
          label="Notes internes"
          value={formData.notes_internes || ''}
          onChange={(e) => setFormData({ ...formData, notes_internes: e.target.value })}
          multiline
          rows={3}
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="auto_generate"
            checked={formData.auto_generate}
            onChange={(e) =>
              setFormData({ ...formData, auto_generate: e.target.checked })
            }
            className="rounded border-gray-300 text-mint-600 focus:ring-mint-500"
          />
          <label htmlFor="auto_generate" className="text-sm text-gray-700">
            Generer automatiquement les factures
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={createContract.isPending}>
            Creer le contrat
          </Button>
        </div>
      </form>
    </Modal>
  )
}
